import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Public Privacy Policy page.
 * Route: /privacy
 * Required by Apple App Store and Google Play.
 */
export default function PrivacyPolicy() {
  const effectiveDate = '30 April 2026';

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm opacity-80 hover:opacity-100 mb-4">
            <ArrowLeft className="w-4 h-4" /> Back to Taptile
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-sm opacity-80 mt-2">Effective date: {effectiveDate}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8 leading-relaxed">
        <Section title="1. Who we are">
          <p>
            Taptile (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) is a software platform operated by <strong>Dynatex SaaS Co., Th</strong>,
            a company registered in Thailand. We provide a branded mobile and web portal service
            primarily used by schools and member-based organisations.
          </p>
          <p>
            If you have questions about this policy or your personal data, contact us at{' '}
            <a className="text-blue-600 underline" href="mailto:phil@dynatexsaas.com">phil@dynatexsaas.com</a>.
          </p>
        </Section>

        <Section title="2. What this policy covers">
          <p>This policy applies to information we collect when you:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Visit taptile.app or any tenant portal we host (web)</li>
            <li>Install and use the Taptile native iOS app</li>
            <li>Sign up for an admin account or organisation tenancy</li>
            <li>Receive push notifications, emails, or other communications from Taptile</li>
          </ul>
          <p>
            Each organisation using Taptile (a &ldquo;Tenant&rdquo;, e.g. your school) is the <strong>data controller</strong>{' '}
            for their members' personal data. Taptile acts as a <strong>data processor</strong> on the Tenant’s behalf.
          </p>
        </Section>

        <Section title="3. Information we collect">
          <h3 className="font-semibold mt-3 mb-1">A. Information you give us</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Account details: name, email, password (hashed), role, organisation</li>
            <li>Profile information you choose to add (phone, image)</li>
            <li>Contact details synced from your school management system (NexusCMS, Wonde, etc.) where the school admin has authorised this</li>
            <li>Content you create or upload as an admin (tiles, articles, images, notifications)</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">B. Information collected automatically</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Device push notification token (iOS / web push)</li>
            <li>Basic analytics: pages viewed, tiles tapped, notifications opened (no third-party trackers)</li>
            <li>IP address and browser type, used solely for security and abuse prevention</li>
            <li>Crash and diagnostic logs from the iOS app (via Expo)</li>
          </ul>

          <h3 className="font-semibold mt-4 mb-1">C. Information we do NOT collect</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>We do not access your camera, microphone, location, contacts, photos, or files unless you explicitly upload them.</li>
            <li>We do not use third-party advertising trackers, Facebook Pixel, Google Ads, or similar.</li>
            <li>We do not sell or rent your personal data to anyone, ever.</li>
          </ul>
        </Section>

        <Section title="4. How we use your information">
          <ul className="list-disc pl-6 space-y-1">
            <li>To provide the Taptile service to your organisation</li>
            <li>To authenticate you and protect your account</li>
            <li>To deliver push notifications, emails, and content sent by your organisation’s admins</li>
            <li>To improve product quality (aggregated, non-identifying)</li>
            <li>To comply with legal obligations (e.g. responding to lawful requests)</li>
          </ul>
        </Section>

        <Section title="5. Lawful basis (UK / EU GDPR &amp; Thailand PDPA)">
          <p>We rely on the following lawful bases for processing:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Contract</strong> &mdash; to deliver the service you (or your organisation) signed up for</li>
            <li><strong>Legitimate interests</strong> &mdash; for security, fraud prevention, and product improvement</li>
            <li><strong>Consent</strong> &mdash; for push notifications (you can revoke any time in your device settings)</li>
            <li><strong>Legal obligation</strong> &mdash; when required by law</li>
          </ul>
          <p>
            We comply with the UK and EU General Data Protection Regulation (GDPR) for users in the UK and EEA,
            and Thailand&rsquo;s Personal Data Protection Act (PDPA) as the operating jurisdiction of Dynatex SaaS Co., Th.
          </p>
        </Section>

        <Section title="6. Sharing your information">
          <p>We share data only with the following categories of recipients:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Your organisation’s admins</strong> &mdash; they can see contacts, push tokens, and notification analytics for their tenant</li>
            <li><strong>Sub-processors we use to run the service</strong> &mdash; listed in Section 8</li>
            <li><strong>Legal authorities</strong> &mdash; only when compelled by lawful process</li>
          </ul>
          <p>We never sell your data. We never use it to train machine-learning models.</p>
        </Section>

        <Section title="7. Children">
          <p>
            Taptile is intended for use by individuals aged 13 and over. Children under 13 should not create
            an account. Where Taptile is used in a school context, students under 13 may be enrolled by their
            school’s administrator under the school’s own data-protection arrangements; Taptile processes
            that data strictly on the school’s behalf.
          </p>
        </Section>

        <Section title="8. Sub-processors">
          <p>We use trusted third-party services to operate Taptile:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>MongoDB Atlas</strong> &mdash; primary database (EU region)</li>
            <li><strong>DigitalOcean</strong> &mdash; image storage (Frankfurt region)</li>
            <li><strong>SendGrid (Twilio)</strong> &mdash; transactional email</li>
            <li><strong>Expo</strong> &mdash; iOS push notification delivery and build infrastructure</li>
            <li><strong>Apple Push Notification Service (APNs)</strong> &mdash; iOS push delivery</li>
            <li><strong>Stripe</strong> &mdash; payment processing (for paid tenant subscriptions)</li>
            <li><strong>Google</strong> &mdash; SSO authentication (only if you choose &ldquo;Continue with Google&rdquo;)</li>
          </ul>
        </Section>

        <Section title="9. International transfers">
          <p>
            Your data is stored primarily in the EU. Some sub-processors (e.g. Apple, Expo, Stripe) may transfer
            data to the United States under appropriate safeguards including Standard Contractual Clauses and
            the EU-US Data Privacy Framework where applicable.
          </p>
        </Section>

        <Section title="10. Data retention">
          <ul className="list-disc pl-6 space-y-1">
            <li>Account data: kept while your account is active. Deleted within 30 days of account closure.</li>
            <li>Notification history: kept for 12 months, then deleted.</li>
            <li>Backups: kept for 30 days then permanently deleted.</li>
            <li>Audit logs: kept for 24 months for security and compliance purposes.</li>
          </ul>
        </Section>

        <Section title="11. Your rights">
          <p>Under UK/EU GDPR and Thailand’s PDPA you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Access the personal data we hold about you</li>
            <li>Have inaccurate data corrected</li>
            <li>Have your data erased (subject to legal retention requirements)</li>
            <li>Restrict or object to processing</li>
            <li>Receive your data in a portable format</li>
            <li>Withdraw consent at any time</li>
            <li>Lodge a complaint with your local data protection authority — for example the UK Information Commissioner’s Office (ICO) for UK residents, your national supervisory authority for EU residents, or Thailand’s Personal Data Protection Committee (PDPC)</li>
          </ul>
          <p>
            To exercise these rights, contact{' '}
            <a className="text-blue-600 underline" href="mailto:phil@dynatexsaas.com">phil@dynatexsaas.com</a>{' '}
            or, where Taptile processes data on behalf of your school, contact your school's data protection officer first.
          </p>
        </Section>

        <Section title="12. Security">
          <p>We use industry-standard measures to protect your data, including:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>HTTPS encryption for all data in transit</li>
            <li>Encrypted password storage (bcrypt)</li>
            <li>Per-tenant data isolation</li>
            <li>API keys hashed and individually scoped</li>
            <li>Regular security reviews</li>
          </ul>
        </Section>

        <Section title="13. Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be announced via in-app notice
            or email. The effective date at the top of this page indicates the most recent update.
          </p>
        </Section>

        <Section title="14. Contact us">
          <p>
            Questions, requests, or concerns:{' '}
            <a className="text-blue-600 underline" href="mailto:phil@dynatexsaas.com">phil@dynatexsaas.com</a>
          </p>
          <p>
            Dynatex SaaS Co., Th<br />
            Registered in Thailand
          </p>
        </Section>

        <div className="pt-8 border-t text-sm text-slate-500">
          <Link to="/" className="text-blue-600 underline">&larr; Return to taptile.app</Link>
          {' \u00B7 '}
          <Link to="/terms" className="text-blue-600 underline">Terms of Service</Link>
          {' \u00B7 '}
          <Link to="/support" className="text-blue-600 underline">Support</Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold text-slate-900 mt-6 mb-2 border-b-2 border-orange-500 pb-1">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
